"use client";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui";
import { TimeField } from "@/components/ui/FormFields/TimeFormField/TimeField";
import { useForwardedRef } from "@/hooks/useForwardedRef";
import { cn } from "@/lib/utils/cn";
import { parseTime } from "@internationalized/date";
import dayjs from "dayjs";
import React, { useRef } from "react";
import type { AriaTimeFieldProps } from "react-aria";
import { type TimeValue } from "react-aria";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

const TimeFormField = React.forwardRef<
  HTMLDivElement,
  AriaTimeFieldProps<TimeValue> & {
    name: string;
    register: UseFormRegisterReturn;
    label?: string;
    clearable?: boolean;
    suggestedDate?: Date;
    suggestedDateText?: string;
  }
>(({ name, ...props }, forwardedRef) => {
  const ref = useForwardedRef(forwardedRef);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isInDisabledFieldset = Boolean(
    buttonRef.current?.closest("fieldset:disabled"),
  );

  const form = useFormContext();
  // Helper to coerce string -> TimeValue
  const toTimeValue = (val?: string): TimeValue | undefined => {
    if (!val) return undefined;
    // try to format to HH:mm:ss using dayjs then parse
    const hms = dayjs(val).isValid() ? dayjs(val).format("HH:mm:ss") : val;
    try {
      return parseTime(hms);
    } catch {
      return undefined;
    }
  };

  return (
    <FormField
      control={form.control}
      disabled={isInDisabledFieldset}
      {...form.register(name)}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => (
        <FormItem>
          <div className="flex">
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormMessage
              data-testid="error-message"
              className="grow text-right"
            />
          </div>
          <FormControl>
            <div ref={ref} className={cn("relative flex items-center")}>
              <TimeField
                value={toTimeValue(field.value as string)}
                isDisabled={props.isDisabled || isInDisabledFieldset}
                granularity={props.granularity}
                hourCycle={props.hourCycle}
                onChange={(value) => {
                  if (!value) {
                    field.onChange("");
                    return;
                  }
                  field.onChange(value.toString());
                }}
              />
            </div>
          </FormControl>
        </FormItem>
      )}
    />
  );
});

TimeFormField.displayName = "TimeFormField";

export { TimeFormField };
